(() => {
  const roots = document.querySelectorAll('[data-ff-recommender-root]');
  if (!roots.length) return;

  function normalizeQuestions(rawQuestions, configuredStartQuestionId) {
    const questionMap = {};

    if (Array.isArray(rawQuestions)) {
      rawQuestions.forEach((question, index) => {
        if (!question?.id) return;

        const fallbackNextQuestionId = rawQuestions[index + 1]?.id || null;
        questionMap[question.id] = {
          ...question,
          options: (question.options || []).map((option) => ({
            ...option,
            next_question_id: option.next_question_id || fallbackNextQuestionId || null
          }))
        };
      });

      return {
        questionMap,
        startQuestionId: configuredStartQuestionId || rawQuestions[0]?.id || null
      };
    }

    if (rawQuestions && typeof rawQuestions === 'object') {
      Object.entries(rawQuestions).forEach(([questionId, question]) => {
        if (!question) return;
        questionMap[questionId] = {
          ...question,
          id: question.id || questionId,
          options: question.options || []
        };
      });

      return {
        questionMap,
        startQuestionId: configuredStartQuestionId || Object.keys(questionMap)[0] || null
      };
    }

    return { questionMap, startQuestionId: null };
  }

  function inferPurpose(answers) {
    if (answers.purpose) return answers.purpose;
    if (answers.placement === 'desk') return 'office';
    return 'home';
  }

  function inferPlacement(answers) {
    if (answers.placement) return answers.placement;

    const byPurpose = {
      gift: 'console',
      hosting: 'dining_table',
      office: 'desk',
      unsure: 'living_room',
      home: 'living_room'
    };

    return byPurpose[inferPurpose(answers)] || 'living_room';
  }

  function inferPresence(answers) {
    if (answers.presence_surface) return answers.presence_surface;
    if (answers.presence_general) return answers.presence_general;

    const byLargePresence = {
      quiet_touch: 'small',
      focal_point: 'balanced',
      dramatic_anchor: 'statement'
    };

    if (answers.presence_large) {
      return byLargePresence[answers.presence_large] || 'balanced';
    }

    const byGiftDirection = {
      classic: 'balanced',
      warm_personal: 'balanced',
      bold: 'statement'
    };

    return byGiftDirection[answers.gift_direction] || 'balanced';
  }

  function inferStyle(answers) {
    const byMood = {
      calm_clean: 'minimal',
      soft_romantic: 'romantic',
      natural_warm: 'earthy',
      deep_dramatic: 'dramatic',
      bright_fresh: 'colorful'
    };

    if (answers.mood) {
      return byMood[answers.mood] || 'minimal';
    }

    const byGiftDirection = {
      classic: 'minimal',
      warm_personal: 'romantic',
      bold: 'dramatic'
    };

    return byGiftDirection[answers.gift_direction] || 'minimal';
  }

  function inferColorMood(answers) {
    const byMood = {
      calm_clean: 'neutral',
      soft_romantic: 'blush',
      natural_warm: 'warm',
      deep_dramatic: 'deep',
      bright_fresh: 'bright'
    };

    if (answers.mood) {
      return byMood[answers.mood] || 'neutral';
    }

    const byGiftDirection = {
      classic: 'neutral',
      warm_personal: 'warm',
      bold: 'deep'
    };

    return byGiftDirection[answers.gift_direction] || 'neutral';
  }

  function buildLegacyQuiz(answers) {
    return {
      intent: inferPurpose(answers),
      placement: inferPlacement(answers),
      style: inferStyle(answers),
      color_mood: inferColorMood(answers),
      presence: inferPresence(answers),
      budget_tier: answers.budget_tier || 'unknown',
      placement_known: answers.placement_known || null,
      purpose: answers.purpose || null,
      mood: answers.mood || null,
      gift_direction: answers.gift_direction || null
    };
  }

  roots.forEach((root) => {
    const configId = root.getAttribute('data-config-id');
    const configNode = document.getElementById(configId);
    if (!configNode) return;

    const config = JSON.parse(configNode.textContent || '{}');
    const strings = config.strings || {};
    const answers = {};
    const { questionMap, startQuestionId } = normalizeQuestions(config.questions, config.start_question_id);

    if (!startQuestionId || !questionMap[startQuestionId]) return;

    let currentQuestionId = startQuestionId;
    let selectedValue = null;
    let visitedQuestionIds = [startQuestionId];

    const timelineNode = root.querySelector('[data-ff-timeline]');
    const questionNode = root.querySelector('[data-ff-question]');
    const optionsNode = root.querySelector('[data-ff-options]');
    const nextButton = root.querySelector('[data-ff-next]');
    const stepNode = root.querySelector('[data-ff-step]');
    const resultNode = root.querySelector('[data-ff-result]');
    const resultCardNode = root.querySelector('[data-ff-result-card]');
    const statusNode = root.querySelector('[data-ff-status]');
    const visualizerNode = root.querySelector('[data-ff-visualizer]');
    const visualizerFrame = root.querySelector('[data-ff-visualizer-frame]');
    const assistNode = root.querySelector('[data-ff-assist]');

    function getQuestion(questionId) {
      return questionMap[questionId] || null;
    }

    function getCurrentIndex() {
      return visitedQuestionIds.indexOf(currentQuestionId);
    }

    function clearAnswersAfter(questionId) {
      const questionIndex = visitedQuestionIds.indexOf(questionId);
      if (questionIndex < 0) return;

      const removedIds = visitedQuestionIds.slice(questionIndex + 1);
      removedIds.forEach((id) => {
        delete answers[id];
      });

      visitedQuestionIds = visitedQuestionIds.slice(0, questionIndex + 1);
    }

    function goToStep(questionId) {
      const questionIndex = visitedQuestionIds.indexOf(questionId);
      if (questionIndex < 0 || questionId === currentQuestionId) {
        return;
      }

      currentQuestionId = questionId;
      renderQuestion();
    }

    function renderTimeline() {
      if (!timelineNode) return;

      const currentIndex = getCurrentIndex();
      timelineNode.innerHTML = '';

      visitedQuestionIds.forEach((questionId, index) => {
        const question = getQuestion(questionId);
        if (!question) return;

        const item = document.createElement('button');
        const isCurrent = index === currentIndex;
        const isCompleted = index < currentIndex && Boolean(answers[questionId]);
        const shortLabel = question.short_label || question.shortLabel || question.label;

        item.type = 'button';
        item.className = 'ff-rec-progress-step';
        item.disabled = isCurrent;

        if (isCurrent) {
          item.classList.add('is-active');
          item.setAttribute('aria-current', 'step');
        } else if (isCompleted) {
          item.classList.add('is-completed');
        }

        item.innerHTML = `
          <span class="ff-rec-progress-dot" data-progress-number="${index + 1}" aria-hidden="true"></span>
          <span class="ff-rec-progress-label">${shortLabel}</span>
        `;

        if (!isCurrent) {
          item.addEventListener('click', () => {
            goToStep(questionId);
          });
        }

        timelineNode.appendChild(item);
      });
    }

    function setStatus(message) {
      statusNode.textContent = message;
      statusNode.classList.remove('hidden');
    }

    function clearStatus() {
      statusNode.textContent = '';
      statusNode.classList.add('hidden');
    }

    function showAssist() {
      assistNode?.classList.remove('hidden');
    }

    function hideAssist() {
      assistNode?.classList.add('hidden');
    }

    function hideVisualizer() {
      visualizerNode?.classList.add('hidden');
      visualizerFrame?.classList.add('hidden');
    }

    function showVisualizer() {
      visualizerNode?.classList.remove('hidden');
      visualizerFrame?.classList.remove('hidden');
    }

    function getNextQuestionId(question, answerValue) {
      const option = (question.options || []).find((item) => item.value === answerValue);
      return option?.next_question_id || question.next_question_id || null;
    }

    function renderQuestion() {
      clearStatus();

      const question = getQuestion(currentQuestionId);
      if (!question) return;

      selectedValue = answers[currentQuestionId] ?? null;
      questionNode.textContent = question.label;
      nextButton.disabled = !selectedValue;
      renderTimeline();

      optionsNode.innerHTML = '';
      (question.options || []).forEach((option) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ff-rec-option';
        button.textContent = option.label;

        if (option.value === selectedValue) {
          button.classList.add('is-selected');
        }

        button.addEventListener('click', () => {
          const previousValue = answers[currentQuestionId] ?? null;
          if (previousValue && previousValue !== option.value) {
            clearAnswersAfter(currentQuestionId);
          }

          selectedValue = option.value;
          answers[currentQuestionId] = option.value;
          renderQuestion();
        });

        optionsNode.appendChild(button);
      });
    }

    function renderBlockedState(message) {
      stepNode.classList.add('hidden');
      resultNode.classList.remove('hidden');
      hideVisualizer();
      showAssist();
      resultCardNode.innerHTML = `
        <p class="ff-rec-result__eyebrow">${strings.assistEyebrow || 'כאן עדיף ליווי אישי'}</p>
        <h3 class="ff-rec-result__title">כדאי להמשיך לייעוץ אישי</h3>
        <div class="ff-rec-result__meta">${message}</div>
      `;
      setStatus(message);
    }

    function buildRecommendationImage(product, tone) {
      if (!product?.image_url) return '';

      return `
        <div class="ff-rec-result__media ff-rec-result__media--${tone}">
          <img
            class="ff-rec-result__image"
            src="${product.image_url}"
            alt="תמונה של ${product.title || 'ההמלצה'}"
            loading="lazy"
          >
        </div>
      `;
    }

    function buildWhyFitsList(product) {
      const reasons = Array.isArray(product?.why_fits) ? product.why_fits : [];
      if (!reasons.length) return '';

      return `
        <div class="ff-rec-result__why">
          <div class="ff-rec-result__why-title">${strings.whyFits || 'למה זה מתאים לכם'}</div>
          <ul class="ff-rec-result__list">${reasons.map((reason) => `<li>${reason}</li>`).join('')}</ul>
        </div>
      `;
    }

    function buildProductHref(product) {
      if (!product?.handle) return '';
      return `/products/${encodeURIComponent(product.handle)}`;
    }

    function buildRecommendationActions(product) {
      const href = buildProductHref(product);
      if (!href) return '';

      return `
        <div class="ff-rec-result__actions">
          <a class="button button--secondary" href="${href}">${strings.viewProductCta || 'לעמוד המוצר'}</a>
        </div>
      `;
    }

    function buildRecommendationBlock(product, { tone = 'primary', title, summary }) {
      if (!product && !title) return '';

      return `
        <article class="ff-rec-result__recommendation ff-rec-result__recommendation--${tone}">
          ${buildRecommendationImage(product, tone)}
          <div class="ff-rec-result__body">
            <h3 class="ff-rec-result__title ff-rec-result__title--${tone}">${title}</h3>
            <div class="ff-rec-result__summary">${summary}</div>
            ${buildWhyFitsList(product)}
            ${buildRecommendationActions(product)}
          </div>
        </article>
      `;
    }

    function buildVisualizerSrc(product, iframe) {
      const source = iframe?.getAttribute('data-src') || iframe?.getAttribute('src');
      if (!source) return '';

      const url = new URL(source);
      url.searchParams.set('p_name', product?.title || 'המלצת Forever Flowers');
      url.searchParams.set('p_img', product?.image_url || '');
      url.searchParams.set('p_h', String(product?.height_cm ?? 30));
      url.searchParams.set('p_w', String(product?.diameter_cm ?? 30));
      return url.toString();
    }

    function hydrateVisualizer(product) {
      const iframe = visualizerFrame?.querySelector('.ai-visualizer-iframe');
      const triggerText = visualizerFrame?.querySelector('.ai-visualizer-text');
      if (!iframe || !product) return false;

      const nextSrc = buildVisualizerSrc(product, iframe);
      if (!nextSrc) return false;

      iframe.setAttribute('data-src', nextSrc);
      if (iframe.getAttribute('src')) {
        iframe.setAttribute('src', nextSrc);
      }

      if (triggerText) {
        triggerText.textContent = product?.title
          ? `לצפייה בהדמיה של ${product.title} אצלכם בבית`
          : triggerText.textContent;
      }

      return true;
    }

    function renderResult(payload) {
      stepNode.classList.add('hidden');
      resultNode.classList.remove('hidden');
      clearStatus();

      const result = payload.result || {};
      const primaryProduct = payload.primary_product || null;
      const alternateProduct = payload.alternate_product || null;
      const fallbackRoute = result.fallback_route || 'none';
      const showDirectResult = fallbackRoute === 'none' || fallbackRoute === 'compare';
      const eyebrow = fallbackRoute === 'none'
        ? (strings.highConfidenceEyebrow || 'התאמה מובילה')
        : fallbackRoute === 'compare'
        ? (strings.compareEyebrow || 'שתי אפשרויות קרובות')
          : (strings.assistEyebrow || 'כאן עדיף ליווי אישי');

      if (showDirectResult) {
        const primaryTitle = primaryProduct?.title || result.primary_product_id || (strings.resultPending || 'ההמלצה עדיין לא זמינה');
        const primarySummary = primaryProduct?.title
          ? (strings.primarySummary || 'זו ההמלצה שהכי מתאימה כרגע לפי התשובות שלכם.')
          : (strings.resultPending || 'ההמלצה עדיין לא זמינה');
        const primaryBlock = buildRecommendationBlock(primaryProduct, {
          tone: 'primary',
          title: primaryTitle,
          summary: primarySummary
        });
        const alternateBlock = fallbackRoute === 'compare' && alternateProduct?.title
          ? buildRecommendationBlock(alternateProduct, {
            tone: 'secondary',
            title: alternateProduct.title,
            summary: strings.primarySummary || 'זו ההמלצה שהכי מתאימה כרגע לפי התשובות שלכם.'
          })
          : '';

        resultCardNode.innerHTML = `
          <p class="ff-rec-result__eyebrow">${eyebrow}</p>
          <div class="ff-rec-result__stack">
            ${primaryBlock}
            ${alternateBlock}
          </div>
        `;
      } else {
        resultCardNode.innerHTML = `
          <p class="ff-rec-result__eyebrow">${eyebrow}</p>
          <h3 class="ff-rec-result__title">${primaryProduct?.title || result.primary_product_id || 'כדאי להמשיך לייעוץ אישי'}</h3>
          <div class="ff-rec-result__summary">כדי לדייק את הבחירה, כדאי להמשיך לייעוץ אישי.</div>
        `;
      }

      if (showDirectResult && primaryProduct?.visualizer_enabled) {
        const hydrated = hydrateVisualizer(primaryProduct);
        if (hydrated) {
          showVisualizer();
        } else {
          hideVisualizer();
        }
      } else {
        hideVisualizer();
      }

      if (fallbackRoute === 'none') {
        hideAssist();
      } else {
        showAssist();
      }
    }

    async function submitAnswers() {
      if (!config.endpoint) {
        renderBlockedState(strings.serviceUnavailable || 'מנוע ההמלצות עדיין לא זמין.');
        return;
      }

      try {
        setStatus(strings.loading || 'מחפשים את ההתאמה המדויקת...');
        const response = await fetch(config.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            quiz: buildLegacyQuiz(answers),
            quiz_version: config.quiz_version || 'adaptive_v1',
            path_taken: visitedQuestionIds
          })
        });

        if (!response.ok) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const payload = await response.json();
        renderResult(payload);
      } catch (error) {
        console.error(error);
        renderBlockedState(strings.serviceFailed || 'לא הצלחנו להשלים את ההתאמה כרגע.');
      }
    }

    nextButton.addEventListener('click', () => {
      const question = getQuestion(currentQuestionId);
      if (!question || !selectedValue) {
        return;
      }

      answers[currentQuestionId] = selectedValue;

      const nextQuestionId = getNextQuestionId(question, selectedValue);
      if (!nextQuestionId) {
        submitAnswers();
        return;
      }

      if (!getQuestion(nextQuestionId)) {
        renderBlockedState(strings.serviceFailed || 'לא הצלחנו להשלים את ההתאמה כרגע.');
        return;
      }

      clearAnswersAfter(currentQuestionId);
      visitedQuestionIds = [...visitedQuestionIds, nextQuestionId];
      currentQuestionId = nextQuestionId;
      renderQuestion();
    });

    hideAssist();
    hideVisualizer();
    renderQuestion();
  });
})();
